import React, { memo } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import TimeRemaining from './TimeRemaining';
import { t } from '../../services/i18nService';
import styles from './header.scss';
import headerNotificationsSelector from '../../selectors/header/HeaderNotificationsSelector';
import SidebarButton from './buttons/SidebarButton';
import OptionsMenuButton from './buttons/OptionsMenuButton';
import MessageButton from './buttons/MessageButton';
import ConnectionStatus from './ConnectionStatus';
import ChatButton from './buttons/ChatButton';
import OwnPreviewSwitcher from './buttons/OwnPreviewSwitcher';
import InlineSubHeader from './InlineSubHeader';
import BackgroundAudioControlButton from './buttons/BackgroundAudioControlButton';
import a11yIIBScreenReaderService from '../../services/a11yIIBScreenReaderService';
import HeaderA11yService from '../../services/HeaderA11yService';
import settingsReader from '../../store/readHelpers/SettingsReader';

const HeaderNotifications = (props) => {
    const {
        assetRoot,
        candidateInfoString,
        connectionStatus,
        finalMinutesMode,
        hasDelivered,
        isChatEnabled,
        isNoteTakingAndHighlightingEnabled,
        logoPath,
        offlineMode,
        oneMinuteRemaining,
        pauseTime,
        remainingTimePrimaryEndTime,
        showBackgroundAudioControlButton,
        showEndTimePostPrimaryEndTime,
        showLivestreamStatus,
        showMessagesButton,
        showOwnPreviewSwitcher,
        showTimeRemaining,
        timeRemaining,
        ui,
    } = props;

    const isA11yIterationForIIBScreenReaderEnabled = a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
    const topBarClass = styles.topbar + (oneMinuteRemaining ? ` ${styles.oneMinuteRemaining}` : '');

    const {
        connectionContainerClass: a11yConnectionContainerClass,
        centralBarClass: a11yCentralBarClass,
        headerTabIndex,
        headerRole,
    } = HeaderA11yService.getHeaderNotificationsAttributes(isA11yIterationForIIBScreenReaderEnabled);

    const connectionContainerClass = `${styles.connectionAndButtonContainer}${
        a11yConnectionContainerClass ? ` ${styles[a11yConnectionContainerClass]}` : ''
    }`;
    const centralBarClass = `${styles.centralBar}${
        a11yCentralBarClass ? ` ${styles[a11yCentralBarClass]}` : ''
    }`;

    const subHeaderClasses = [styles.subHeaderWrapper];
    if (settingsReader.shouldAllowPauseAndResumeOptionForTestTaker()) {
        subHeaderClasses.push(styles.withPauseResume);
    }

    const renderConnectionAndButtons = () => (
        <div className={connectionContainerClass}>
            <ConnectionStatus
                connectionStatus={connectionStatus}
                offlineMode={offlineMode}
            />
            {showMessagesButton && (
                <MessageButton
                    isDisabled={!!pauseTime}
                />
            )}
            {!hasDelivered && (
                <OptionsMenuButton
                    ui={ui}
                    isDisabled={!!pauseTime}
                />
            )}
            { isChatEnabled && <ChatButton /> }
            { isNoteTakingAndHighlightingEnabled && <SidebarButton isDisabled={!!pauseTime} /> }
        </div>
    );

    return (
        <div className={topBarClass} id="headerTopBar">
            { logoPath &&
                <div className={styles.logo}>
                    <img src={!assetRoot ? logoPath : `${assetRoot}/${logoPath}`} alt={t('header.logo')} />
                </div>
            }

            <div className={styles.subToolbar}>
                { !isA11yIterationForIIBScreenReaderEnabled && renderConnectionAndButtons() }
                <div className={centralBarClass}>
                    <div
                        className={subHeaderClasses.join(' ')}
                        id="candidate-info"
                        role={headerRole}
                    >
                        <div
                            className={styles.statusText}
                            tabIndex={headerTabIndex}
                        >
                            <div className={styles.name}>
                                {candidateInfoString}
                            </div>

                            <div>
                                { showTimeRemaining &&
                                    <TimeRemaining
                                        timeRemaining={timeRemaining}
                                        finalMinutesMode={finalMinutesMode}
                                        isInfinityCausedByLiveSession={showLivestreamStatus}
                                        pauseTime={pauseTime}
                                        remainingTimePrimaryEndTime={remainingTimePrimaryEndTime}
                                        showEndTimePostPrimaryEndTime={showEndTimePostPrimaryEndTime}
                                    />
                                }

                                <InlineSubHeader />
                            </div>
                        </div>

                        { showBackgroundAudioControlButton && <BackgroundAudioControlButton />}
                    </div>

                    { showOwnPreviewSwitcher && <OwnPreviewSwitcher /> }
                </div>
                { isA11yIterationForIIBScreenReaderEnabled && renderConnectionAndButtons() }

                <div className={styles.statusColor} />
            </div>
        </div>
    );
};

HeaderNotifications.propTypes = {
    assetRoot: PropTypes.string.isRequired,
    candidateInfoString: PropTypes.string.isRequired,
    connectionStatus: PropTypes.string,
    finalMinutesMode: PropTypes.bool,
    hasDelivered: PropTypes.bool.isRequired,
    isChatEnabled: PropTypes.bool.isRequired,
    isNoteTakingAndHighlightingEnabled: PropTypes.bool.isRequired,
    logoPath: PropTypes.string,
    offlineMode: PropTypes.bool.isRequired,
    oneMinuteRemaining: PropTypes.bool.isRequired,
    pauseTime: PropTypes.number,
    remainingTimePrimaryEndTime: PropTypes.number,
    showBackgroundAudioControlButton: PropTypes.bool,
    showEndTimePostPrimaryEndTime: PropTypes.bool.isRequired,
    showLivestreamStatus: PropTypes.bool.isRequired,
    showMessagesButton: PropTypes.bool.isRequired,
    showOwnPreviewSwitcher: PropTypes.bool.isRequired,
    showTimeRemaining: PropTypes.bool.isRequired,
    timeRemaining: PropTypes.number,
    ui: PropTypes.object.isRequired,
};

const mapStateToProps = (state, ownProps) => headerNotificationsSelector.selectProps(state, ownProps);
export default memo(connect(mapStateToProps)(HeaderNotifications));
